import React from 'react'
import { useTranslation } from 'react-i18next'
import { Check } from 'lucide-react'
import {
  ContextMenuItem,
  ContextMenuItemTrailing,
  ContextMenuSeparator,
  ContextMenuShortcut,
} from '@/src/renderer/components/ui/context-menu'
import type { ReminderAnchor } from '@/src/renderer/lib/db/models'
import { beforeAnchor } from '@/src/renderer/lib/reminders/schedule'
import { useTaskReminderRows } from '../../hooks/useTaskReminderRows'
import { useReminderActions } from '../../hooks/useReminderActions'
import { relativeReminderLabel, silenceLabel } from '../../utils/reminderLabels'

/** Die Vorschläge im Menü — weniger als im Kasten der Details: das Häufige, der Rest dort. */
const START_OFFSETS = [0, 15, 30, 60] as const
const DEADLINE_OFFSETS = [60, 1440] as const

const clock = (date: Date, locale: string) =>
  new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(date)

export interface ReminderMenuContentProps {
  taskId: string
  /** „Datum & Uhrzeit …“ — öffnet die Aufgaben-Details mit offenem Chip. Ohne: Die Zeile fehlt. */
  onOpenDetails?: () => void
}

/**
 * **Das Unter-Menü „Erinnerung“** im Aufgaben-Menü (Plan task-reminders). Jede Zeile steht immer
 * da; was gesetzt ist, trägt einen Haken, ein zweiter Klick nimmt es weg. Braucht ein Vorschlag
 * eine Uhrzeit, die fehlt, ist er grau und sagt rechts, warum. Feste Zeitpunkte gibt es in den
 * Aufgaben-Details — „Datum & Uhrzeit …“ führt hin.
 */
export const ReminderMenuContent: React.FC<ReminderMenuContentProps> = ({ taskId, onOpenDetails }) => {
  const { t, i18n } = useTranslation()
  const locale = i18n.language || 'de'
  const data = useTaskReminderRows(taskId)
  const actions = useReminderActions(taskId)
  if (!data) return null

  const { rows, anchors, anchorSilence, defaultOffset } = data
  const findRelative = (anchor: ReminderAnchor, offset: number) => rows.find(row =>
    row.reminder.kind === 'relative' && !row.standard && (row.reminder.anchor ?? 'start') === anchor && row.reminder.offsetMinutes === offset)
  const standard = rows.find(row => row.standard)

  const item = (anchor: ReminderAnchor, offset: number) => {
    const existing = findRelative(anchor, offset)
    const at = anchors[anchor]
    const why = anchorSilence[anchor]
    return (
      <ContextMenuItem
        key={`${anchor}-${offset}`}
        // Ein gesetzter bleibt anklickbar — zum Wegnehmen —, auch wenn sein Bezug die Uhrzeit verlor.
        disabled={!existing && (!!why || !at)}
        onSelect={() => (existing ? actions.remove(existing.reminder.id) : actions.add({ kind: 'relative', anchor, offsetMinutes: offset }))}
      >
        <span className="context-menu-content__item-icon">{existing && <Check size={14} strokeWidth={2} />}</span>
        {relativeReminderLabel(anchor, offset, t)}
        <ContextMenuItemTrailing>
          <ContextMenuShortcut>
            {why ? silenceLabel(why, t) : at && clock(beforeAnchor(at, offset), locale)}
          </ContextMenuShortcut>
        </ContextMenuItemTrailing>
      </ContextMenuItem>
    )
  }

  const standardLabel = defaultOffset === null ? t('reminders.label.off') : relativeReminderLabel('start', defaultOffset, t)

  return (
    <>
      {START_OFFSETS.map(offset => item('start', offset))}
      <ContextMenuSeparator />
      {DEADLINE_OFFSETS.map(offset => item('deadline', offset))}
      <ContextMenuSeparator />
      <ContextMenuItem
        disabled={!standard && (!!anchorSilence.start || defaultOffset === null)}
        onSelect={() => (standard ? actions.remove(standard.reminder.id) : actions.add({ kind: 'relative', anchor: 'start', offsetMinutes: null }))}
      >
        <span className="context-menu-content__item-icon">{standard && <Check size={14} strokeWidth={2} />}</span>
        {t('reminders.menu.standard', { label: standardLabel })}
      </ContextMenuItem>
      <ContextMenuSeparator />
      {onOpenDetails && (
        <ContextMenuItem onSelect={onOpenDetails}>
          <span className="context-menu-content__item-icon" />
          {t('reminders.menu.dateTime')}
        </ContextMenuItem>
      )}
      <ContextMenuItem
        disabled={rows.length === 0}
        onSelect={() => rows.forEach(row => actions.remove(row.reminder.id))}
      >
        <span className="context-menu-content__item-icon" />
        {t('reminders.menu.removeAll')}
      </ContextMenuItem>
    </>
  )
}
